(ns ycweb.app.admin.productsync.productsync
  (:require
   [rum.core :as rum]
   [ycweb.app.admin.productsync.api.common :as common]
   [ycweb.app.dashboard.presence :as presence]
   [ycweb.components.buttons :as buttons]
   [ycweb.components.feedback :as feedback]
   [ycweb.components.common :as cmn]
   [ycweb.components.flow :as flow]
   [ycweb.components.forms :as forms]
   [ycweb.components.grid :as grid]
   [ycweb.components.headings :as headings]
   [ycweb.components.toaster :as toaster]
   [ycweb.components.transitions :as t]
   [ycweb.util.comms :as comms]
   [ycweb.util.core :as u]
   [ycweb.util.urlstate :as urlstate]))

(rum/defc reactive-message < rum/reactive [text-atom progress-atom]
  (str (rum/react text-atom) (rum/react progress-atom)))

;//=====================================================================//
;; Here's the like to the spec doc for this project:
;; https://docs.google.com/document/d/17slQ5BdZqlplabfXvyv3ii29shdU6tz87kYGTPQK6ZM/edit?usp=sharing

;//=====================================================================//
;; This is the first "screen" on which you start the process. Pressing the
;; "Get started" button will use the Airtable API to check if there are
;; any changes in the specific view in the Airtable base and get them accordingly.

(rum/defc product-sync < rum/reactive < cmn/cursors [app-atom]
  (let [process-atom (cmn/process-cursor app-atom)
        reverse-atom (rum/cursor-in process-atom [:product-sync/reverse])
        batch-id (cmn/curact reverse-atom [:batch-id])
        dev-mode? (:dev-mode? @app-atom)
        import-type (cmn/curact process-atom [:product-sync/import-type])
        ;; nil until admin.product-sync.config replies; true when this user is
        ;; opted in to reading brands/suppliers from the synced tables.
        synced-tables? (cmn/curact process-atom [:product-sync/config :synced-tables?])]

    [:<>
     [:div.admin-react-wrapper__content
      (headings/heading "Import product content from Airtable" {:type :h1 :size :large})
      [:p.yc-paragraph.u-max-width-720
       "Before syncing product content, please make sure that you have filled in all the required fields (including images) for each SKU in the "
       [:a.text-link
        {:href (str "https://www.airtable.com/" (common/product-upload-base dev-mode?)) :target "_blank"}
        "Product Upload Airtable base"]
       " and that it's all in a state you'd be happy to import. \uD83C\uDF08"]]

     [:label
      {:class (u/bem :form-label [:set])}
      "What are you syncing about?"]
     [:div.form-field
      (rum/with-key
        (forms/radio "Full Product Sync" "full" (cmn/cursor-in process-atom [:product-sync/import-type]))
        "full")]

     [:div.form-field
      (rum/with-key
        (forms/radio "Skeleton Product Sync" "skeleton" (cmn/cursor-in process-atom [:product-sync/import-type]))
        "skeleton")]

     (buttons/button-web "Get Started"
       (merge
         (urlstate/href-props app-atom "product-sync" "brands" {:import-type import-type})
         {:style :primary
          :size :default}))

     (cond
       ;; Opted in to the synced tables: nothing to sync by hand, so no button.
       synced-tables?
       [:div.u-max-width-720
        [:hr {:class (u/bem :u-hr [:default])}]
        (feedback/feedback :info
          "You're on the new brand & supplier source"
          "Brands and suppliers for your imports come straight from the synced Airtable tables, which update automatically whenever a brand or supplier changes in the Admin Dashboard. There is nothing to sync by hand. Everyone else, and the nightly sync, still use the legacy tables.")]

       ;; Legacy path: the nightly sync maintains the brand/supplier tables and
       ;; the button forces it early when a brand or supplier was just added.
       (false? synced-tables?)
       [:<>
        [:hr {:class (u/bem :u-hr [:default])}]

        (buttons/button-web "Sync Brands & Suppliers"
          (merge
            (urlstate/href-on-click #(grid/dispatch-action app-atom :admin.product-sync/sync-airtable.brandsandsuppliers {}))
            {:style :secondary
             :size :default}))

        [:div
         [:p.yc-paragraph
          "Do you want to sync brands and suppliers? "
          [:a.text-link {:href "#"} "Find out more"]
          "."]
         ;; TODO: We need a show/hide setup on this [:p]. Probably something around the process-atom.
         [:p.yc-paragraph
          "Choose this option if you need to sync the brand and supplier info in Airtable with brands and suppliers that exist in our Admin Dashboard. You'd use this when you've created a new brand or we've added a new supplier and want to make sure it's available in Airtable to import product content. Even though this sync runs automatically each night, we can only import product content for brands and suppliers which have been synced, so you might need this in a hurry."]]])

     [:hr {:class (u/bem :u-hr [:default])}]

     [:div
      [:label
       {:class (u/bem :form-label [:set])}
       "Something went whoops!"]

      (rum/with-key
        (forms/input "" "Batch ID"
          (cmn/cursor-in reverse-atom [:batch-id])
          {:type :text
           :name :batch-id
           :id :batch-id
           :class "u-max-width-160"})
        "batch-id")

      (buttons/button-web "Reverse Product Sync"
        (merge
          (urlstate/href-on-click
            #(cond
               (zero? (count batch-id))
               (toaster/open-toast app-atom :error "Please put in a Batch ID." {})

               (< (count batch-id) 10)
               (toaster/open-toast app-atom :error "Your Batch ID must be 10 or more digits." {})

               :else
               (urlstate/redirect app-atom "product-sync" "reverse")))
          {:type :span-button
           :style :secondary
           :disabled (< (count batch-id) 10)}))]]))

(defmethod grid/action :admin.product-sync/sync-airtable.brandsandsuppliers [app-atom _ _]
  ;; The handler is synchronous and does two full Airtable table scans plus
  ;; writes at a 3/second throttle, so the processing toast has to outlive it.
  (toaster/open-toast app-atom :processing "Syncing Brands & Suppliers" {:closetime 300000})
  (comms/send :admin.product-sync.sync-airtable/brandsandsuppliers {}))

(defmethod comms/receive :admin.product-sync.sync-airtable/brandsandsuppliers [app-atom e]
  (if (:success e)
    (toaster/open-toast app-atom :success (or (:msg e) "Brands & Suppliers synced") {:closetime 30000})
    (toaster/open-toast app-atom :error (:msg e) {})))

(defmethod comms/receive :admin.product-sync/config [app-atom e]
  (reset! (rum/cursor-in (cmn/process-cursor app-atom) [:product-sync/config]) e))

(defmethod t/transition-open :admin.product-sync [app-atom _ _]
  (swap! (cmn/process-cursor app-atom)
    #(-> %
       (assoc-in [:product-sync/brands :brands] [])
       (assoc :product-sync/import-type "full"
              :product-sync/config nil)))
  (comms/send :admin.product-sync/config {}))

(defmethod t/transition-after-open :admin.product-sync [app-atom _ _]
  (presence/open app-atom {:context/ident :product-sync}))

(defmethod flow/render-screen :admin.product-sync [app-atom _]
  (product-sync app-atom))